import { useCallback, useEffect } from 'react';
import { Form } from '@components/Form';
import { OrderSourceFormFields } from '@components/OrderSource/Sections/OrderSourceFormFields';
import { useCreateOrderSource } from '@hooks/orderSources';
import { useTranslation } from '@hooks/useTranslation';
import type { OrderSource } from '@api/orderSources';
import {
  createOrderSourceFormSchema,
  type CreateOrderSourceFormData,
} from '@/react/schemas/orderSource.schema';
import { SubmitButton } from './SubmitButton';

interface CreateSourceFormProps {
  onSuccess: (source: OrderSource) => void;
  onPendingChange?: (isPending: boolean) => void;
}

function FormFooter() {
  const { t } = useTranslation();

  return (
    <div className="-mx-8 border-t border-dinnerplate-white px-8 py-6">
      <div className="flex justify-end">
        <SubmitButton label={t('create_source.actions.create')} />
      </div>
    </div>
  );
}

export function CreateSourceForm({ onSuccess, onPendingChange }: CreateSourceFormProps) {
  const createOrderSource = useCreateOrderSource();

  useEffect(() => {
    onPendingChange?.(createOrderSource.isPending);
    return () => onPendingChange?.(false);
  }, [createOrderSource.isPending, onPendingChange]);

  const handleSubmit = useCallback(
    async (values: CreateOrderSourceFormData) => {
      try {
        const source = await createOrderSource.mutateAsync({
          name: values.name,
          description: values.description,
          fee: values.fee,
          fee_type: values.fee_type,
        });
        onSuccess(source);
      } catch {
        // Error toast surfaced by useCreateOrderSource's onError; swallow
        // here so the rejection doesn't bubble out of the form's submit handler.
      }
    },
    [createOrderSource, onSuccess],
  );

  return (
    <Form<CreateOrderSourceFormData>
      schema={createOrderSourceFormSchema}
      defaultValues={{
        name: '',
        description: null,
        fee: null as unknown as number,
        fee_type: null as unknown as CreateOrderSourceFormData['fee_type'],
      }}
      onSubmit={handleSubmit}
      isPending={createOrderSource.isPending}
      className="flex flex-col gap-8 overflow-x-hidden px-8 pt-6 font-inter"
    >
      <OrderSourceFormFields />
      <FormFooter />
    </Form>
  );
}
